import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, Client, ClientsListResponse, CurrentAgent } from '../lib/api';
import { relativeTime, absoluteTime, bytes, shortHash } from '../lib/format';
import { ErrorState } from '../components/QueryState';

type View = 'overview' | 'inventory';
type SortDir = 'asc' | 'desc';
type SortKey =
  | 'uuid' | 'hostname' | 'last_seen' | 'status' | 'tags'
  | 'os' | 'kernel' | 'cpu_model' | 'cpu_cores' | 'ram_total' | 'ram_used_pct' | 'disk_used_pct' | 'agent' | 'inventory_at';

export function ClientsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view: View = searchParams.get('view') === 'inventory' ? 'inventory' : 'overview';
  const setView = (v: View) => {
    const next = new URLSearchParams(searchParams);
    if (v === 'overview') next.delete('view'); else next.set('view', v);
    setSearchParams(next, { replace: true });
  };

  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [blocked, setBlocked] = useState('');
  const [tag, setTag] = useState('');
  const [hasInventory, setHasInventory] = useState<'' | 'yes' | 'no'>('');

  const [sortKey, setSortKey] = useState<SortKey>('last_seen');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (status) params.set('status', status);
  if (blocked) params.set('blocked', blocked);
  if (tag) params.set('tag', tag);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['clients', q, status, blocked, tag],
    queryFn: () => api.get<ClientsListResponse>(`/clients/?${params.toString()}`),
  });
  const currentAgentScriptSha = data?.current_agent?.script?.sha256 ?? null;

  const allTags = useMemo(() => {
    if (!data) return [];
    const s = new Set<string>();
    for (const c of data.clients) for (const t of c.tags) s.add(t);
    return [...s].sort();
  }, [data]);

  const sorted = useMemo(() => {
    if (!data) return [];
    const rows = data.clients
      .filter((c) => {
        if (view !== 'inventory' || hasInventory === '') return true;
        return hasInventory === 'yes' ? !!c.inventory_at : !c.inventory_at;
      });
    const cmp = compareFor(sortKey, sortDir);
    return [...rows].sort((a, b) => cmp(a, b));
  }, [data, sortKey, sortDir, hasInventory, view]);

  const qc = useQueryClient();
  const toggleBlock = useMutation({
    mutationFn: ({ uuid, blocked }: { uuid: string; blocked: boolean }) =>
      api.post(`/clients/${uuid}/${blocked ? 'unblock' : 'block'}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
  const del = useMutation({
    mutationFn: (uuid: string) => api.delete(`/clients/${uuid}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  function toggleSort(k: SortKey) {
    if (sortKey === k) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir(k === 'last_seen' || k === 'inventory_at' ? 'desc' : 'asc');
    }
  }
  const reportedAgent = (c: Client) => c.agent_sha256 || c.inventory?.agent?.host_customize_sha256 || null;
  const agentStatus = (c: Client): 'current' | 'behind' | 'unknown' => {
    const reported = reportedAgent(c);
    if (!reported || !currentAgentScriptSha) return 'unknown';
    return reported === currentAgentScriptSha ? 'current' : 'behind';
  };
  const behindCount = useMemo(() => {
    if (!data || !currentAgentScriptSha) return 0;
    return data.clients.filter((c) => agentStatus(c) === 'behind').length;
  }, [data, currentAgentScriptSha]);
  const noSignalCount = useMemo(() => {
    if (!data) return 0;
    return data.clients.filter((c) => !reportedAgent(c) && c.last_seen).length;
  }, [data]);
  const SortHeader = ({ k, children }: { k: SortKey; children: React.ReactNode }) => (
    <th>
      <button className="inline-flex items-center gap-1 hover:text-fg" onClick={() => toggleSort(k)}>
        {children}
        <span className="text-xs text-muted">{sortKey === k ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Clients</h1>
      <div className="border-b border-border flex gap-2 text-sm">
        <button className={tabClass(view === 'overview')} onClick={() => setView('overview')}>Overview</button>
        <button className={tabClass(view === 'inventory')} onClick={() => setView('inventory')}>Inventory</button>
      </div>
      {isError && <ErrorState error={error} onRetry={() => refetch()} />}
      {toggleBlock.isError && <div className="text-danger text-sm">{toggleBlock.error.message}</div>}
      {del.isError && <div className="text-danger text-sm">{del.error.message}</div>}
      <div className="flex flex-wrap gap-2 items-end">
        <label className="text-sm">
          <div className="text-muted">Search</div>
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="uuid or hostname" />
        </label>
        <label className="text-sm">
          <div className="text-muted">Status</div>
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">any</option>
            <option value="ok">ok</option>
            <option value="error">error</option>
          </select>
        </label>
        <label className="text-sm">
          <div className="text-muted">Blocked</div>
          <select className="select" value={blocked} onChange={(e) => setBlocked(e.target.value)}>
            <option value="">any</option>
            <option value="true">blocked</option>
            <option value="false">not blocked</option>
          </select>
        </label>
        <label className="text-sm">
          <div className="text-muted">Tag</div>
          <select className="select" value={tag} onChange={(e) => setTag(e.target.value)}>
            <option value="">any</option>
            {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        {view === 'inventory' && (
          <label className="text-sm">
            <div className="text-muted">Has inventory</div>
            <select className="select" value={hasInventory} onChange={(e) => setHasInventory(e.target.value as '' | 'yes' | 'no')}>
              <option value="">any</option>
              <option value="yes">yes</option>
              <option value="no">no</option>
            </select>
          </label>
        )}
        <button className="btn" onClick={() => refetch()}>Refresh</button>
      </div>
      <div className="card overflow-auto">
        {view === 'overview' ? (
          <table className="table">
            <thead>
              <tr>
                <SortHeader k="uuid">UUID</SortHeader>
                <SortHeader k="hostname">Hostname</SortHeader>
                <SortHeader k="last_seen">Last seen</SortHeader>
                <SortHeader k="status">Status</SortHeader>
                <SortHeader k="tags">Tags</SortHeader>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="text-muted">Loading…</td></tr>}
              {data?.clients.length === 0 && (
                <tr><td colSpan={6} className="text-muted">No clients yet. Onboard a host with the install script.</td></tr>
              )}
              {sorted.map((c) => (
                <tr key={c.uuid}>
                  <td className="font-mono text-xs">
                    <Link className="text-accent hover:underline" to={`/clients/${c.uuid}`}>
                      {c.uuid.slice(0, 8)}…
                    </Link>
                  </td>
                  <td>{c.hostname || <span className="text-muted">—</span>}</td>
                  <td title={absoluteTime(c.last_seen ?? undefined)}>{relativeTime(c.last_seen ?? undefined)}</td>
                  <td>
                    <StatusBadge c={c} />
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {c.tags.map((t) => <span key={t} className="badge">{t}</span>)}
                    </div>
                  </td>
                  <td className="space-x-2 whitespace-nowrap">
                    <button className="btn" onClick={() => toggleBlock.mutate({ uuid: c.uuid, blocked: c.blocked })}>
                      {c.blocked ? 'Unblock' : 'Block'}
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => {
                        if (confirm(`Delete client ${c.uuid}? It will re-register on next check-in.`)) del.mutate(c.uuid);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <>
            <AgentBanner
              currentAgent={data?.current_agent ?? null}
              behindCount={behindCount}
              noSignalCount={noSignalCount}
            />
            <table className="table">
              <thead>
                <tr>
                  <SortHeader k="hostname">Hostname</SortHeader>
                  <SortHeader k="os">OS</SortHeader>
                  <SortHeader k="kernel">Kernel</SortHeader>
                  <SortHeader k="cpu_model">CPU</SortHeader>
                  <SortHeader k="cpu_cores">Cores</SortHeader>
                  <SortHeader k="ram_total">RAM</SortHeader>
                  <SortHeader k="ram_used_pct">RAM used</SortHeader>
                  <SortHeader k="disk_used_pct">Disk worst</SortHeader>
                  <SortHeader k="agent">Agent</SortHeader>
                  <SortHeader k="inventory_at">Reported</SortHeader>
                  <SortHeader k="status">Status</SortHeader>
                </tr>
              </thead>
              <tbody>
                {isLoading && <tr><td colSpan={11} className="text-muted">Loading…</td></tr>}
                {!isLoading && sorted.length === 0 && (
                  <tr><td colSpan={11} className="text-muted">No clients matched. Add the inventory action to the manifest to start collecting data.</td></tr>
                )}
                {sorted.map((c) => {
                  const inv = c.inventory;
                  const worstFs = pickWorstFs(c);
                  return (
                    <tr key={c.uuid}>
                      <td>
                        <Link className="text-accent hover:underline" to={`/clients/${c.uuid}`}>
                          {c.hostname || c.uuid.slice(0, 8) + '…'}
                        </Link>
                      </td>
                      <td>{inv?.os?.name ? `${inv.os.name} ${inv.os.version || ''}`.trim() : <span className="text-muted">—</span>}</td>
                      <td className="font-mono text-xs">{inv?.os?.kernel || <span className="text-muted">—</span>}</td>
                      <td className="max-w-xs truncate" title={inv?.cpu?.model || ''}>{inv?.cpu?.model || <span className="text-muted">—</span>}</td>
                      <td>{inv?.cpu?.cores ?? <span className="text-muted">—</span>}</td>
                      <td>{inv?.memory?.total ? bytes(inv.memory.total) : <span className="text-muted">—</span>}</td>
                      <td>{inv?.memory?.used_pct !== undefined ? <UsagePct pct={inv.memory.used_pct} /> : <span className="text-muted">—</span>}</td>
                      <td>{worstFs ? <UsagePct pct={worstFs.used_pct} title={`${worstFs.mount}`} /> : <span className="text-muted">—</span>}</td>
                      <td><AgentCell status={agentStatus(c)} reported={reportedAgent(c)} hasCheckedIn={!!c.last_seen} /></td>
                      <td title={absoluteTime(c.inventory_at ?? undefined)}>{relativeTime(c.inventory_at ?? undefined)}</td>
                      <td><StatusBadge c={c} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

function tabClass(active: boolean) {
  return active
    ? 'px-3 py-2 border-b-2 border-accent text-accent font-medium'
    : 'px-3 py-2 text-muted hover:text-fg';
}

function StatusBadge({ c }: { c: Client }) {
  if (c.blocked) return <span className="badge badge-blocked">blocked</span>;
  if (c.last_status === 'error') return <span className="badge badge-error">error</span>;
  if (c.last_status === 'ok') return <span className="badge badge-ok">ok</span>;
  return <span className="badge">never</span>;
}

function UsagePct({ pct, title }: { pct: number; title?: string }) {
  const cls = pct >= 90 ? 'text-danger' : pct >= 75 ? 'text-fg' : 'text-muted';
  return <span className={cls} title={title}>{pct.toFixed(1)}%</span>;
}

function pickWorstFs(c: Client) {
  const fs = c.inventory?.filesystems;
  if (!fs || fs.length === 0) return null;
  return fs.reduce((w, f) => (f.used_pct > (w?.used_pct ?? -1) ? f : w), fs[0]);
}

function compareFor(key: SortKey, dir: SortDir): (a: Client, b: Client) => number {
  const cmpStr = (a: string, b: string) => a.localeCompare(b);
  const cmpNum = (a: number, b: number) => a - b;
  const applyDir = (cmp: number) => (dir === 'asc' ? cmp : -cmp);
  const nullsLast = <T,>(va: T | null | undefined, vb: T | null | undefined, fn: (a: T, b: T) => number) => {
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    return applyDir(fn(va, vb));
  };
  switch (key) {
    case 'uuid': return (a, b) => applyDir(cmpStr(a.uuid, b.uuid));
    case 'hostname': return (a, b) => nullsLast(a.hostname, b.hostname, cmpStr);
    case 'last_seen': return (a, b) => nullsLast(a.last_seen, b.last_seen, cmpNum);
    case 'status': return (a, b) => applyDir(cmpStr(statusRank(a), statusRank(b)));
    case 'tags': return (a, b) => applyDir(cmpStr(a.tags.join(','), b.tags.join(',')));
    case 'os': return (a, b) => applyDir(cmpStr((a.inventory?.os?.name || '') + (a.inventory?.os?.version || ''), (b.inventory?.os?.name || '') + (b.inventory?.os?.version || '')));
    case 'kernel': return (a, b) => applyDir(cmpStr(a.inventory?.os?.kernel || '', b.inventory?.os?.kernel || ''));
    case 'cpu_model': return (a, b) => applyDir(cmpStr(a.inventory?.cpu?.model || '', b.inventory?.cpu?.model || ''));
    case 'cpu_cores': return (a, b) => nullsLast(a.inventory?.cpu?.cores, b.inventory?.cpu?.cores, cmpNum);
    case 'ram_total': return (a, b) => nullsLast(a.inventory?.memory?.total, b.inventory?.memory?.total, cmpNum);
    case 'ram_used_pct': return (a, b) => nullsLast(a.inventory?.memory?.used_pct, b.inventory?.memory?.used_pct, cmpNum);
    case 'disk_used_pct': return (a, b) => nullsLast(pickWorstFs(a)?.used_pct, pickWorstFs(b)?.used_pct, cmpNum);
    case 'agent': return (a, b) => applyDir(cmpStr(a.agent_sha256 || a.inventory?.agent?.host_customize_sha256 || '', b.agent_sha256 || b.inventory?.agent?.host_customize_sha256 || ''));
    case 'inventory_at': return (a, b) => nullsLast(a.inventory_at, b.inventory_at, cmpNum);
  }
}

function statusRank(c: Client) {
  if (c.blocked) return '1-blocked';
  if (c.last_status === 'error') return '2-error';
  if (c.last_status === 'ok') return '3-ok';
  return '4-never';
}

function AgentBanner({
  currentAgent,
  behindCount,
  noSignalCount,
}: {
  currentAgent: CurrentAgent | null;
  behindCount: number;
  noSignalCount: number;
}) {
  const sha = currentAgent?.script?.sha256;
  if (!sha) return null;
  return (
    <div className="text-xs px-3 py-2 border-b border-border flex items-baseline gap-2">
      <span className="text-muted">Current agent:</span>
      <span className="font-mono" title={sha}>{shortHash(sha)}</span>
      {behindCount > 0 && (
        <span className="badge badge-error" title="Hosts reporting a different host-customize sha256">
          {behindCount} {behindCount === 1 ? 'host' : 'hosts'} behind
        </span>
      )}
      {noSignalCount > 0 && (
        <span
          className="badge badge-error"
          title="Hosts that have checked in but never reported their agent sha256 — running a pre-self-update agent. Re-run install.sh on these hosts."
        >
          {noSignalCount} {noSignalCount === 1 ? 'host' : 'hosts'} pre-self-update
        </span>
      )}
    </div>
  );
}

function AgentCell({
  status,
  reported,
  hasCheckedIn,
}: {
  status: 'current' | 'behind' | 'unknown';
  reported: string | null;
  hasCheckedIn: boolean;
}) {
  if (!reported) {
    if (hasCheckedIn) {
      return (
        <span title="Host has checked in but never reported its agent sha256 — running a pre-self-update agent.">
          <span className="text-muted">—</span>
          <span className="badge badge-error ml-1">needs reinstall</span>
        </span>
      );
    }
    return <span className="text-muted">—</span>;
  }
  return (
    <span title={reported}>
      <span className="font-mono text-xs">{shortHash(reported)}</span>
      {status === 'current' && <span className="badge badge-ok ml-1">current</span>}
      {status === 'behind' && <span className="badge badge-error ml-1">behind</span>}
    </span>
  );
}
